import { useState, useEffect, useRef } from "react";
import Navbar from "../../components/headerSection/navbar/Navbar";
import { Link, useLocation } from "react-router-dom";
import FormInput from "../../components/formInput/FormInput";
import { useTranslation } from "react-i18next";
import registerService from "../../services/registerService";
import loginService from "../../services/loginService"; // Importiere den loginService
import resetPasswordService from "../../services/resetPasswordService";
import Maintenance from "../maintenance/Maintenance";

function Login() {
  const [inputValue, setInputValue] = useState({ email: "", password: "" });
  const [resetEmail, setResetEmail] = useState("");
  const [message, setMessage] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const hasTokenBeenProcessed = useRef(false); // Verwende useRef, um den Zustand zu speichern
  // Feature flag to show/hide the "Passwort vergessen?" button.
  // Set VITE_SHOW_RESET_BUTTON=true in your .env to show it; default is hidden.
  const SHOW_RESET_BUTTON = import.meta.env.VITE_SHOW_RESET_BUTTON === "false";
  
  // Wartungsmodus nur für Login-Seite
  const maintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === "true";
  
  if (maintenanceMode) {
    return <Maintenance />;
  }

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token");

    // Verhindere mehrfache Ausführung des Bestätigungscalls
    if (token && !hasTokenBeenProcessed.current) {
      hasTokenBeenProcessed.current = true; // Setze das Flag sofort, um weitere Aufrufe zu verhindern
      setIsProcessing(true);

      registerService
        .confirmUser(token)
        .then((responseMessage) => {
          console.debug("Login.jsx: Bestätigungsantwort:", responseMessage);
          if (responseMessage === "SUCCESS") {
            setMessage(t("register.confirmationSuccess"));
          } else {
            setMessage(t("register.invalidToken"));
          }
        })
        .catch(() => {
          setMessage(t("register.invalidToken"));
        })
        .finally(() => {
          setIsProcessing(false);
          // Entferne den Token aus der URL, um erneutes Ausführen des `useEffect` zu verhindern
          window.history.replaceState({}, document.title, "/login");
        });
    }
  }, [location.search, t]);

  const handleLoginClick = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      const response = await loginService.login(
        inputValue.email,
        inputValue.password
      );

      if (response.errorCode === "INVALID_CREDENTIALS") {
        setMessage(t("error.invalidCredentials"));
        setIsProcessing(false);
        return;
      }

      console.debug("Login.jsx: response.data.token: " + response.data.token);

      if (response.data.token) {
        console.debug(
          "Login von Landing Page App war erfolgreich. Token erhalten."
        );

        const jobSourceUrl = import.meta.env.VITE_JOBSOURCE_FRONTEND_URL;

        // Send the GET request with token as query parameter
        const urlWithToken = `${jobSourceUrl}?token=${response.data.token}`;
        
        // Clear storage asynchronously without blocking redirect
        try {
          localStorage.clear();
          sessionStorage.clear();
        } catch (e) {
          console.error("Failed to clear storage:", e);
        }
        
        window.location.href = urlWithToken; // Redirect to jobSourceUrl with token as query parameter
        // Note: setIsProcessing(false) not needed here as page will unload
      }
    } catch (error) {
      setMessage(t("login.loginFailed"));
      setIsProcessing(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      await resetPasswordService.sendResetEmail(resetEmail);
      setResetMessage(
        "Wir haben Ihnen eine E-Mail mit Anweisungen zum Zurücksetzen Ihres Passworts gesendet."
      );
      setIsProcessing(false);
    } catch (error) {
      setResetMessage(
        "Fehler beim Senden der E-Mail. Bitte versuchen Sie es erneut."
      );
      setIsProcessing(false);
    }
  };

  return (
    <div className="poppins">
      <Navbar />
      <div className="w-full min-h-screen flex items-center md:mb-20 mb-10">
        <div className="project-container w-full h-full px-4">
          <div className="md:flex justify-center items-center">
            <div className="w-full lg:w-8/12">
              <h2 className="text-3xl md:text-4xl mb-8 lg:text-[50px] lg:text-start text-center font-semibold">
                {t("loginH")}
              </h2>
              <h4 className="text-2xl lg:text-[35px] lg:text-start text-center font-medium mb-10 lg:mb-16">
                {t("loginDesc")}
              </h4>
              <p className="text-black mb-2 lg:text-start text-center text-sm md:text-base">
                {t("dontHaveAccount")}
              </p>
              <p className="lg:text-start text-center text-sm md:text-base">
                {t("youCan")}{" "}
                <a
                  href="/demo"
                  className="text-[#4D47C3] font-semibold"
                >
                  {t("registerHere")}
                </a>
              </p>
            </div>

            <div className="w-full lg:w-4/12 md:mt-0 mt-10">
              {!showPasswordReset ? (
                <>
                  <h2 className="text-3xl font-medium lg:mb-6 lg:text-start text-center">
                    {t("loginH")}
                  </h2>

                  {message && <p className="mt-4 text-center">{message}</p>}

                  <form>
                    <FormInput
                      type="email"
                      placeholder={t("login.enterEmail")}
                      value={inputValue.email}
                      onChange={(e) =>
                        setInputValue((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      className="w-full py-5 px-5 bg-inputBgColor rounded-[9px] focus:outline-none mt-4 placeholder:text-[15px] placeholder:text-[#A7A3FF] placeholder:font-normal"
                      name="email"
                    />
                    <FormInput
                      type="password"
                      placeholder={t("password")}
                      value={inputValue.password}
                      onChange={(e) =>
                        setInputValue((prev) => ({
                          ...prev,
                          password: e.target.value,
                        }))
                      }
                      className="w-full py-5 px-5 bg-inputBgColor rounded-[9px] focus:outline-none mt-4 placeholder:text-[15px] placeholder:text-[#A7A3FF] placeholder:font-normal"
                      name="password"
                    />
                    {SHOW_RESET_BUTTON && (
                      <p className="text-end text-[13px] text-[#B0B0B0] mt-4">
                        <button
                          type="button"
                          onClick={() => setShowPasswordReset(true)}
                          className="hover:text-[#4D47C3] transition-colors duration-200"
                        >
                          Passwort vergessen?
                        </button>
                      </p>
                    )}
                    <button
                      type="submit"
                      onClick={handleLoginClick}
                      className="w-full bg-btnBgColor text-white py-5 rounded-[9px] focus:outline-none focus:ring mt-6 shadow-logRegBtnShadow hover:bg-opacity-90 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t("login.loginProcessing") || "Wird verarbeitet..."}
                        </span>
                      ) : t("loginH")}
                    </button>
                  </form>
                </>
              ) : (
                <>
                  <h2 className="text-3xl font-medium lg:mb-6 lg:text-start text-center">
                    Passwort zurücksetzen
                  </h2>

                  <p className="text-[#A6A6A6] text-center mb-6">
                    Wir senden Ihnen eine E-Mail mit Anweisungen zum
                    Zurücksetzen Ihres Passworts.
                  </p>

                  {resetMessage && (
                    <p className="mt-4 text-center text-green-600 bg-green-50 p-3 rounded-lg">
                      {resetMessage}
                    </p>
                  )}

                  <form onSubmit={handlePasswordReset}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-Mail
                    </label>
                    <FormInput
                      type="email"
                      placeholder="Ihre E-Mail-Adresse"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="w-full py-5 px-5 bg-inputBgColor rounded-[9px] focus:outline-none placeholder:text-[15px] placeholder:text-[#A7A3FF] placeholder:font-normal"
                      name="resetEmail"
                      required
                    />

                    <button
                      type="submit"
                      className="w-full bg-btnBgColor text-white py-5 rounded-[9px] focus:outline-none focus:ring mt-6 shadow-logRegBtnShadow hover:bg-opacity-90 transition-all duration-300"
                      disabled={isProcessing}
                    >
                      {isProcessing ? "Wird gesendet..." : "E-Mail senden"}
                    </button>
                  </form>

                  <p className="text-center text-[13px] text-[#B0B0B0] mt-6">
                    Hier aus Versehen?{" "}
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordReset(false);
                        setResetMessage("");
                        setResetEmail("");
                      }}
                      className="text-[#4D47C3] font-semibold hover:underline"
                    >
                      Zurück zum Login
                    </button>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
